import {
  ErrorResponse,
  TimeclockDeviceMeResponse,
  TimeclockDevicePunchResponse,
  type TimeclockPunchKind,
} from '@aeris/contracts'

/**
 * O aparelho que bate o ponto (F339): o token dele e as três chamadas.
 *
 * Não passa pelo `request` do api-client de propósito: aquele manda o cookie
 * da sessão e, num 401, desloga a pessoa. Aqui não há pessoa — a credencial é
 * a do aparelho, vai só no cabeçalho, e o 401 quer dizer "aparelho revogado".
 */

const STORAGE_KEY = 'aeris.ponto.aparelho'
const TOKEN_PREFIX = 'aeris_ponto_'

/** O token salvo, ou nulo. O armazenamento pode faltar (aba anônima). */
export const storedDeviceToken = (): string | null => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY)
    return value?.startsWith(TOKEN_PREFIX) === true ? value : null
  } catch {
    return null
  }
}

export const forgetDeviceToken = (): void => {
  for (const employeeId of [...photos.keys()]) dropPhoto(employeeId)
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Sem armazenamento, não havia o que esquecer.
  }
}

/**
 * Lê o token do link de pareamento (`/bater-ponto#aeris_ponto_…`), guarda e
 * limpa o endereço. O token vem no fragmento porque o fragmento nunca vai ao
 * servidor — não aparece em log de ninguém.
 */
export const pairFromLocation = (): string | null => {
  const hash = window.location.hash.replace(/^#/u, '')
  if (!hash.startsWith(TOKEN_PREFIX)) return storedDeviceToken()
  try {
    window.localStorage.setItem(STORAGE_KEY, hash)
  } catch {
    // Sem armazenamento o aparelho vale só enquanto a aba ficar aberta.
  }
  window.history.replaceState(null, '', window.location.pathname)
  return hash
}

export class DeviceError extends Error {
  readonly status: number
  readonly code: string
  constructor(status: number, code: string, message: string) {
    super(message)
    this.status = status
    this.code = code
  }
}

/** Sem internet, ou o servidor não respondeu: nada foi gravado. */
export class DeviceOfflineError extends Error {}

const send = async (
  token: string,
  path: string,
  init: { method?: 'GET' | 'POST'; body?: FormData; signal?: AbortSignal } = {}
): Promise<Response> => {
  let response: Response
  try {
    response = await fetch(`/api/v1/timeclock/device${path}`, {
      method: init.method ?? 'GET',
      credentials: 'omit',
      headers: { accept: 'application/json', authorization: `Bearer ${token}` },
      ...(init.body === undefined ? {} : { body: init.body }),
      ...(init.signal === undefined ? {} : { signal: init.signal }),
    })
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') throw error
    throw new DeviceOfflineError('offline')
  }
  if (response.ok) return response
  const parsed = ErrorResponse.safeParse(
    await response.json().catch(() => null)
  )
  if (!parsed.success) throw new DeviceOfflineError(String(response.status))
  throw new DeviceError(response.status, parsed.data.code, parsed.data.message)
}

export const fetchDeviceMe = async (
  token: string,
  signal?: AbortSignal
): Promise<TimeclockDeviceMeResponse> =>
  TimeclockDeviceMeResponse.parse(
    await (
      await send(token, '/me', signal === undefined ? {} : { signal })
    ).json()
  )

/**
 * As miniaturas já baixadas, por pessoa, com a chave da foto (o checksum).
 * A grade remonta depois de cada batida: sem isto, baixava todas de novo.
 * São menos de 20 por loja, então ficam na memória da página.
 */
const photos = new Map<
  string,
  { readonly key: string; readonly url: Promise<string | null> }
>()

const dropPhoto = (employeeId: string): void => {
  const old = photos.get(employeeId)
  photos.delete(employeeId)
  void old?.url.then((url) => {
    if (url !== null) URL.revokeObjectURL(url)
  })
}

/**
 * A miniatura de referência, como endereço local (`blob:`), ou nulo. Vem do
 * cache enquanto a `photoKey` for a mesma; a foto trocada baixa de novo.
 */
export const fetchDeviceMemberPhoto = async (
  token: string,
  employeeId: string,
  photoKey: string
): Promise<string | null> => {
  const cached = photos.get(employeeId)
  if (cached?.key === photoKey) return await cached.url
  if (cached !== undefined) dropPhoto(employeeId)
  const url = (async () => {
    try {
      const response = await send(token, `/members/${employeeId}/photo`)
      return URL.createObjectURL(await response.blob())
    } catch {
      return null
    }
  })()
  photos.set(employeeId, { key: photoKey, url })
  const loaded = await url
  // A falha não fica guardada: a próxima montagem tenta de novo.
  if (loaded === null && photos.get(employeeId)?.url === url) {
    photos.delete(employeeId)
  }
  return loaded
}

export const sendPunch = async (
  token: string,
  input: {
    readonly employeeId: string
    readonly pin: string
    /** Só quando a pessoa trocou o sugerido; nulo, o servidor deduz. */
    readonly kind: TimeclockPunchKind | null
    readonly location: {
      readonly latitude: number
      readonly longitude: number
      readonly accuracy: number
    } | null
    readonly photo: Blob
  }
): Promise<TimeclockDevicePunchResponse> => {
  const body = new FormData()
  // Os dados antes da foto: o servidor lê os campos que chegaram antes dela.
  body.append(
    'data',
    JSON.stringify({
      employeeId: input.employeeId,
      pin: input.pin,
      ...(input.kind === null ? {} : { kind: input.kind }),
      ...(input.location ?? {}),
    })
  )
  body.append('file', input.photo, 'batida.jpg')
  return TimeclockDevicePunchResponse.parse(
    await (await send(token, '/punch', { method: 'POST', body })).json()
  )
}

export const PUNCH_LABELS: Record<TimeclockPunchKind, string> = {
  entrada: 'Entrada',
  saida_almoco: 'Saída pro almoço',
  volta_almoco: 'Volta do almoço',
  saida: 'Saída',
}
